import React, { useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';

const Gallery = () => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const [lightbox, setLightbox] = useState(null);

  const images = [
    {
      src: 'https://images.unsplash.com/photo-1542601906990-b4d3fb778b95?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      thumb: 'https://images.unsplash.com/photo-1542601906990-b4d3fb778b95?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      title: 'Bosque Nativo Alto Paraná',
      category: 'Conservación',
    },
    {
      src: 'https://images.unsplash.com/photo-1502082553048-876760111969?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      thumb: 'https://images.unsplash.com/photo-1502082553048-876760111969?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      title: 'Reforestación en Progreso',
      category: 'Proyectos',
    },
    {
      src: 'https://images.unsplash.com/photo-1518173946687-a4c036bc3c95?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      thumb: 'https://images.unsplash.com/photo-1518173946687-a4c036bc3c95?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      title: 'Amanecer en la Selva',
      category: 'Naturaleza',
    },
    {
      src: 'https://images.unsplash.com/photo-1502082553048-876760111969?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      thumb: 'https://images.unsplash.com/photo-1502082553048-876760111969?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      title: 'Equipo de Campo',
      category: 'Operaciones',
    },
    {
      src: 'https://images.unsplash.com/photo-1625246333195-78d9c38ad449?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      thumb: 'https://images.unsplash.com/photo-1625246333195-78d9c38ad449?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      title: 'Agricultura Regenerativa',
      category: 'Suelos',
    },
    {
      src: 'https://images.unsplash.com/photo-1587595431973-160d0d94add1?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      thumb: 'https://images.unsplash.com/photo-1587595431973-160d0d94add1?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      title: 'Humedales del Pantanal',
      category: 'Biodiversidad',
    },
    {
      src: 'https://images.unsplash.com/photo-1502082553048-876760111969?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      thumb: 'https://images.unsplash.com/photo-1502082553048-876760111969?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      title: 'Comunidad Local',
      category: 'Social',
    },
    {
      src: 'https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      thumb: 'https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      title: 'Vista Aérea del Proyecto',
      category: 'Drone',
    },
  ];

  const openLightbox = (index) => setLightbox(index);
  const closeLightbox = () => setLightbox(null);
  const nextImage = () => setLightbox((prev) => (prev + 1) % images.length);
  const prevImage = () => setLightbox((prev) => (prev - 1 + images.length) % images.length);

  return (
    <section id="galeria" style={{
      background: 'linear-gradient(180deg, var(--dark-900) 0%, var(--dark) 100%)',
      padding: 'var(--space-3xl) 0',
      position: 'relative',
    }}>
      <div className="container" ref={ref}>
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto 60px' }}
        >
          <span style={{
            display: 'inline-block',
            padding: '8px 20px',
            background: 'rgba(16, 185, 129, 0.1)',
            border: '1px solid rgba(16, 185, 129, 0.2)',
            borderRadius: '50px',
            color: 'var(--primary-400)',
            fontSize: '0.85rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '2px',
            marginBottom: '20px',
          }}>
            Galería
          </span>
          <h2 style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            marginBottom: '20px',
          }}>
            Nuestra Operación en{' '}
            <span className="text-gradient">Imágenes</span>
          </h2>
          <p style={{
            color: 'var(--text-secondary)',
            fontSize: '1.1rem',
            lineHeight: 1.7,
          }}>
            Un vistazo a nuestras instalaciones, proyectos forestales y el impacto 
            positivo en las comunidades locales.
          </p>
        </motion.div>

        {/* Gallery Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gridTemplateRows: 'repeat(2, 280px)',
          gap: '16px',
        }} className="gallery-grid">
          {images.map((img, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              style={{
                position: 'relative',
                borderRadius: '16px',
                overflow: 'hidden',
                cursor: 'pointer',
                gridColumn: i === 0 ? 'span 2' : i === 3 ? 'span 2' : 'span 1',
                gridRow: i === 0 ? 'span 2' : 'span 1',
              }}
              className="gallery-item"
              onClick={() => openLightbox(i)}
            >
              <img
                src={img.thumb}
                alt={img.title}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  transition: 'transform 0.5s ease',
                }}
                className="gallery-img"
              />
              <div style={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 50%)',
                opacity: 0,
                transition: 'opacity 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                padding: '24px',
              }} className="gallery-overlay">
                <span style={{
                  fontSize: '0.75rem',
                  color: 'var(--primary-400)',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  marginBottom: '4px',
                }}>
                  {img.category}
                </span>
                <span style={{
                  fontSize: '1.1rem',
                  fontWeight: 600,
                }}>
                  {img.title}
                </span>
              </div>
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                opacity: 0,
                transition: 'opacity 0.3s ease',
              }} className="gallery-zoom">
                <ZoomIn size={32} style={{ color: 'white' }} />
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.95)',
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onClick={closeLightbox}
          >
            <button
              onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
              style={{
                position: 'absolute',
                top: '24px',
                right: '24px',
                background: 'none',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                zIndex: 10,
              }}
            >
              <X size={32} />
            </button>

            <button
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
              style={{
                position: 'absolute',
                left: '24px',
                background: 'rgba(255,255,255,0.1)',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                width: '50px',
                height: '50px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ChevronLeft size={28} />
            </button>

            <motion.img
              key={lightbox}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              src={images[lightbox].src}
              alt={images[lightbox].title}
              style={{
                maxWidth: '90vw',
                maxHeight: '85vh',
                objectFit: 'contain',
                borderRadius: '8px',
              }}
              onClick={(e) => e.stopPropagation()}
            />

            <button
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
              style={{
                position: 'absolute',
                right: '24px',
                background: 'rgba(255,255,255,0.1)',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                width: '50px',
                height: '50px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ChevronRight size={28} />
            </button>

            <div style={{
              position: 'absolute',
              bottom: '40px',
              textAlign: 'center',
            }}>
              <p style={{ color: 'white', fontSize: '1.1rem', fontWeight: 600 }}>
                {images[lightbox].title}
              </p>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                {lightbox + 1} / {images.length}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        .gallery-item:hover .gallery-img {
          transform: scale(1.1);
        }
        .gallery-item:hover .gallery-overlay {
          opacity: 1 !important;
        }
        .gallery-item:hover .gallery-zoom {
          opacity: 1 !important;
        }
        @media (max-width: 1024px) {
          .gallery-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            grid-template-rows: repeat(4, 220px) !important;
          }
          .gallery-item {
            grid-column: span 1 !important;
            grid-row: span 1 !important;
          }
        }
        @media (max-width: 640px) {
          .gallery-grid {
            grid-template-columns: 1fr !important;
            grid-template-rows: repeat(8, 250px) !important;
          }
        }
      `}</style>
    </section>
  );
};

export default Gallery;
